import { array } from 'fp-ts';
import { useCallback, useMemo } from 'react';
import styled from 'styled-components';

import { PrimaryButton, BUTTON_SIZES } from 'ui/button';
import { Modal, ModalFooter, ModalBody, ModalHeader, MODAL_SIZES } from 'ui/modal';
import { fontWeights } from 'ui/theme';
import { useToastNotification } from 'utils/hooks/useToastNotification';

import { MessageType, SubscriptionPolicy } from '../../domain';
import { useUserDisplayName, useUserKey } from '../../hooks';
import { ModalState, SubscribeToMessageTypesModalState, useModalsContext } from '../contexts';
import {
  useFilteredModalState,
  useMessageTypeOrTypes,
  useModifyMessageTypeSubscriptionsMutation,
} from '../hooks';

const displayText = {
  title: 'Subscribe to message type?',
  bulkTitle: 'Subscribe to message types?',
  confirm: 'Subscribe',
  loading: 'Subscribing...',
};

const Bold = styled.b`
  font-weight: ${fontWeights.bold};
`;

const isSubscribingToMessageTypes = (
  modalState?: ModalState | null,
): modalState is SubscribeToMessageTypesModalState =>
  ((modalState as SubscribeToMessageTypesModalState | undefined)?.subscribingToMessageTypes
    ?.length ?? 0) > 0;

const getMessageTypes = (state: SubscribeToMessageTypesModalState) =>
  state.subscribingToMessageTypes;

const isDoubleOptIn = (types: MessageType[]) =>
  array.some((t: MessageType) => t.subscriptionPolicy !== SubscriptionPolicy.doubleOptIn)(types);

export const SubscribeToMessageTypesModal = () => {
  const userKey = useUserKey(true);
  const { hideModal } = useModalsContext();
  const subscribingTypes = useFilteredModalState(isSubscribingToMessageTypes, getMessageTypes);
  const userDisplayName = useUserDisplayName();

  const isDoubleOptInType = useMemo(() => isDoubleOptIn(subscribingTypes), [subscribingTypes]);
  const {
    isLoading,
    isSuccess,
    isError,
    reset,
    mutate: modifySubscription,
  } = useModifyMessageTypeSubscriptionsMutation(isDoubleOptInType);

  const messageTypeOrTypes = useMessageTypeOrTypes(subscribingTypes);

  const onDone = useCallback(() => {
    hideModal();
    reset();
  }, [hideModal, reset]);

  useToastNotification({
    enabled: messageTypeOrTypes.length > 0 && (isSuccess || isError),
    onDone,
    positive: isSuccess,
    negative: isError,
    positiveMessage: (
      <>
        Subscribed <Bold>{userDisplayName}</Bold> to <Bold>{messageTypeOrTypes}</Bold>
      </>
    ),
    negativeMessage: (
      <>
        Could not subscribe <Bold>{userDisplayName}</Bold> to <Bold>{messageTypeOrTypes}</Bold>
      </>
    ),
  });

  if (subscribingTypes.length === 0) {
    return null;
  }

  const handleSubmit = () => {
    modifySubscription({
      userKey,
      messageTypeIds: subscribingTypes.map(({ id }) => id),
      subscribe: true,
    });
  };

  return (
    <Modal
      id="SubscribeToMessageTypesModal"
      isModalOpen
      onClose={hideModal}
      size={MODAL_SIZES.SMALL}
    >
      <ModalHeader>
        {subscribingTypes.length === 1 ? displayText.title : displayText.bulkTitle}
      </ModalHeader>
      <ModalBody>
        Are you sure that you want to subscribe <Bold>{userDisplayName}</Bold> to{' '}
        <Bold>{messageTypeOrTypes}</Bold>?
      </ModalBody>
      <ModalFooter>
        <PrimaryButton
          onClick={handleSubmit}
          data-test="confirm-subscribe-button"
          isDisabled={isLoading}
          size={BUTTON_SIZES.LARGE}
        >
          {isLoading ? displayText.loading : displayText.confirm}
        </PrimaryButton>
      </ModalFooter>
    </Modal>
  );
};
